import { useCallback, useMemo } from 'react';
import { MarketTypeGroup, SelectedData } from '../components/molecules/MarketGroupCategories';
import useLocalStorage from './useLocalStorage';

const MARKET_GROUP_STORAGE_KEY = 'inplay_market_group';

const useMarketGroupStorage = (
  sportId: string | undefined,
  enableMarketGroups: boolean,
  defaultMarketGroup?: MarketTypeGroup,
  marketGroupCategories?: { marketTypeGroups: MarketTypeGroup[] }[]
) => {
  const [marketGroupsBySport, setMarketGroupsBySport] = useLocalStorage<Record<string, SelectedData>>(
    MARKET_GROUP_STORAGE_KEY,
    {}
  );

  const selectedData = useMemo(() => {
    if (!sportId || !enableMarketGroups) return undefined;

    const storedData = marketGroupsBySport[sportId];

    if (storedData && marketGroupCategories?.length) {
      const firstCategory = marketGroupCategories[0];

      // Checks if the stored market group is still available
      if (firstCategory) {
        const marketGroupExists = firstCategory.marketTypeGroups.some((group) => group.id === storedData.id);

        if (marketGroupExists) {
          return { ...storedData, index: 0 };
        }
      }
    }

    return defaultMarketGroup ? { ...defaultMarketGroup, index: 0 } : undefined;
  }, [sportId, enableMarketGroups, defaultMarketGroup, marketGroupsBySport, marketGroupCategories]);

  const handleSelectedData = useCallback(
    (data: SelectedData | undefined) => {
      if (!sportId || !data) return;

      setMarketGroupsBySport((prev) => ({
        ...prev,
        [sportId]: data,
      }));
    },
    [sportId, setMarketGroupsBySport]
  );

  return { selectedData, handleSelectedData };
};

export default useMarketGroupStorage;
